import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from 'src/app/core/services/user.service';
import { RequestsService } from 'src/app/core/services/http/requests.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { DebounceService } from 'src/app/core/services/debounce.service';
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from 'ngx-toastr';
import { DialogService } from 'src/app/core/services/dialog.service';
import { Urls } from 'src/app/lib/urls';
import { ApiService } from 'src/app/services/api.service';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { ChangeDetectionService } from 'src/app/services/change-detection.service';
import { urlValidator } from 'src/app/shared/validators/url-validator';

@Component({
  selector: 'app-features-announcement-edit',
  templateUrl: './features-announcement-edit.component.html',
  styleUrls: ['./features-announcement-edit.component.scss']
})
export class FeaturesAnnouncementEditComponent implements OnInit, OnDestroy {

  public updateForm: UntypedFormGroup;
  isLoading: boolean;
  selected_redirection: string | Blob;
  uid: any;

  // image
  img: any;
  imageFile: {link: string, file: any, name: string};
  file: any;

  // Store original values for change detection
  originalValues: any = {};
  
  // Original image URL from server
  originalImageUrl = '';
  
  // Date validation
  minDateTo: Date | null = null;
  
  // unsubscribe cleanup
  private destroy$ = new Subject<void>();

  constructor(
    private modal: NgxSmartModalService,
    private route: ActivatedRoute,
    private router: Router,
    private fb : UntypedFormBuilder,
    private request: RequestsService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private dialogService: DialogService,
    public userService: UserService,
    public debounce: DebounceService,
    private apiService: ApiService,
    private changeDetectionService: ChangeDetectionService
  ) {
    this.initializeForm()
  }

  ngOnInit(): void {
    this.uid = this.route.snapshot.paramMap.get('id');
    this.get_announcement();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectFile(event) {
    if (event.target.files && event.target.files[0]) {
      const reader = new FileReader();
      const link = event.srcElement.files[0];
      const name = event.srcElement.files[0].name;
      reader.onload = (event: any) => {
        this.imageFile = {
            link: event.target.result,
            file: link,
            name
        };
        this.img = event.target.result;
      };
      reader.readAsDataURL(event.target.files[0]);
    }
    console.log('New file selected:', event.srcElement.files[0].name);
  }

  initializeForm(){
    this.updateForm = this.fb.group({
      webViewUrl: ['', [Validators.required, urlValidator]],
      description: ['', Validators.required],
      dateFrom: ['', Validators.required],
      dateTo: ['', Validators.required],
      img: [''],
    });

    // Subscribe to form changes
    this.updateForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // Validate date range whenever form changes
      this.validateDateRangeForForm();
    });

    // Subscribe to dateFrom changes to update minDateTo
    this.updateForm.get('dateFrom').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(dateFrom => {
        if (dateFrom) {
          // Set minimum date for Date To to be same as Date From
          // Create a new Date object to avoid reference issues
          const minDate = new Date(dateFrom);
          minDate.setHours(0, 0, 0, 0);
          this.minDateTo = minDate;
          
          console.log('Date From changed:', dateFrom);
          console.log('Min Date To set to:', this.minDateTo);
          
          // If Date To is already set and is earlier than Date From, clear it
          const currentDateTo = this.updateForm.get('dateTo').value;
          if (currentDateTo) {
            const dateToCompare = new Date(currentDateTo);
            dateToCompare.setHours(0, 0, 0, 0);
            
            if (dateToCompare < minDate) {
              this.toastr.warning(
                'Date To has been cleared as it was earlier than Date From.',
                'Date Updated'
              );
              this.updateForm.patchValue({ dateTo: null }, { emitEvent: false });
            }
          }
        } else {
          this.minDateTo = null;
        }
      });

    // Real-time date validation
    this.updateForm.controls.dateTo.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.validateDateRange());
  }

  
  async get_announcement(){
    try {
      this.spinner.show().catch((err) => {
        console.error('Spinner show error:', err);
      });

      const response = await this.request.put(
        `${Urls.announcements}/${this.uid}`, 
        {}
      );

      if (response && response.result) {
        this.updateForm.patchValue({
          webViewUrl: response.result.webViewUrl || '',
          description: response.result.description || '',
          dateFrom: response.result.dateFrom || '',
          dateTo: response.result.dateTo || '',
        });
        
        // Set minDateTo based on loaded dateFrom
        if (response.result.dateFrom) {
          const minDate = new Date(response.result.dateFrom);
          minDate.setHours(0, 0, 0, 0);
          this.minDateTo = minDate;
          console.log('Initial minDateTo set to:', this.minDateTo);
        }
        
        // Store original image
        this.img = response.result.imageUrl;
        this.originalImageUrl = response.result.imageUrl;
        
        // Store original values AFTER form is populated
        this.storeOriginalValues();
      }

      this.spinner.hide().catch((err) => {
        console.error('Spinner hide error:', err);
      });

    } catch (error) {
      this.toastr.error('Failed to load announcement', 'Error!');
      this.spinner.hide().catch((err) => {
        console.error('Spinner hide error:', err);
      });
    }
  }


  storeOriginalValues() {
    const valuesToStore = {
      webViewUrl: this.updateForm.controls.webViewUrl.value || '',
      description: this.updateForm.controls.description.value || '',
      // Convert dates to YYYY-MM-DD format for comparison (ignore time)
      dateFrom: this.normalizeDate(this.updateForm.controls.dateFrom.value),
      dateTo: this.normalizeDate(this.updateForm.controls.dateTo.value),
      imageUrl: this.originalImageUrl,
      hasNewImage: false // Track if user selected a new image
    };

    // Use the service to create a deep clone
    this.originalValues = this.changeDetectionService.cloneValues(valuesToStore);
  }

  getCurrentValues(): any {
    return {
      webViewUrl: this.updateForm.controls.webViewUrl.value || '',
      description: this.updateForm.controls.description.value || '',
      // Convert dates to YYYY-MM-DD format for comparison (ignore time)
      dateFrom: this.normalizeDate(this.updateForm.controls.dateFrom.value),
      dateTo: this.normalizeDate(this.updateForm.controls.dateTo.value),
      imageUrl: this.originalImageUrl,
      hasNewImage: !!this.imageFile?.file, // Has user selected a new image?
      isDateRangeValid: this.isDateRangeValid() // Check if date range is valid
    };
  }

  isDateRangeValid(): boolean {
    const dateFrom = this.updateForm.controls.dateFrom.value;
    const dateTo = this.updateForm.controls.dateTo.value;
    
    if (!dateFrom || !dateTo) {
      return true; // If dates aren't set, don't block
    }
    
    const selectedDateFrom = new Date(dateFrom);
    selectedDateFrom.setHours(0, 0, 0, 0);
    
    const selectedDateTo = new Date(dateTo);
    selectedDateTo.setHours(0, 0, 0, 0);
    
    // Date To must be >= Date From
    return selectedDateTo >= selectedDateFrom;
  }


  hasChanges(): boolean {
    const currentValues = this.getCurrentValues();
    const hasFormChanges = this.changeDetectionService.hasChanges(
      this.originalValues, 
      currentValues
    );
    
    return hasFormChanges;
  }

  isSubmitDisabled(): boolean {
    return !this.hasChanges() || this.updateForm.invalid || !this.isDateRangeValid();
  }

  async update() {
    // Validate form
    this.updateForm.markAllAsTouched();
    
    if (this.updateForm.invalid) {
      // Check for specific validation errors
      const webViewUrlControl = this.updateForm.get('webViewUrl');
      
      if (webViewUrlControl.hasError('required')) {
        this.toastr.warning('Please enter URL', 'Required Field Missing');
        return;
      }
      
      if (webViewUrlControl.hasError('invalidUrl')) {
        this.toastr.warning('Please enter a valid URL (must start with http:// or https://)', 'Invalid URL Format');
        return;
      }

      const descriptionControl = this.updateForm.get('description');
      if (descriptionControl.hasError('required')) {
        this.toastr.warning('Please enter Description', 'Required Field Missing');
        return;
      }
      
      this.toastr.warning('Please fill in all required fields', 'Validation Error');
      return;
    }

    // Validate date range BEFORE submission
    const dateFrom = this.updateForm.controls.dateFrom.value;
    const dateTo = this.updateForm.controls.dateTo.value;
    
    if (!dateFrom) {
      this.toastr.warning('Please select Date From', 'Required Field Missing');
      return;
    }

    if (!dateTo) {
      this.toastr.warning('Please select Date To', 'Required Field Missing');
      return;
    }
    
    if (dateFrom && dateTo) {
      const selectedDateFrom = new Date(dateFrom);
      selectedDateFrom.setHours(0, 0, 0, 0);
      
      const selectedDateTo = new Date(dateTo);
      selectedDateTo.setHours(0, 0, 0, 0);
      
      // Validate Date To is not before Date From
      if (selectedDateTo < selectedDateFrom) {
        this.toastr.warning(
          'Date To must be equal to or after Date From', 
          'Invalid Date Range'
        );
        return;
      }
    }

    if (!this.hasChanges()) {
      this.toastr.info('No changes detected', 'Info');
      return;
    }

    try {
      this.spinner.show().catch((err) => {
        console.error('Spinner show error:', err);
      });

      const formData = new FormData();
      
      const url = this.updateForm.controls.webViewUrl.value;
      // Process URL - keep the original protocol (http or https)
      // if (url.includes('https://')) {
      //   url = url.replace("https://", "www.");
      // } else if (url.includes('http://')) {
      //   url = url.replace("http://", "www.");
      // }

      formData.append('webViewUrl', url);
      formData.append(
        'dateFrom', 
        moment.utc(this.updateForm.controls.dateFrom.value).format('YYYY-MM-DDTHH:mm:ss')
      );
      formData.append(
        'dateTo', 
        moment.utc(this.updateForm.controls.dateTo.value).format('YYYY-MM-DDTHH:mm:ss')
      );
      formData.append('description', this.updateForm.controls.description.value);

      // Only append image if new one was selected
      if (this.imageFile?.file) {
        formData.append('image', this.imageFile.file);
        console.log('New image will be uploaded:', this.imageFile.name);
      }

      // Use the API service method
      const response = await this.apiService.updateAnnouncement(formData, this.uid);


      if (response.status === 200) {
        this.toastr.success('Announcement updated successfully!', 'Success!');
        
        // Clear the image file reference
        this.imageFile = null;
        
        // Navigate back to list
        this.router.navigate(['/features/announcement']);
      } else {
        const errorMsg = response?.error?.message || 'Failed to update announcement';
        this.toastr.error(errorMsg, 'Update Failed!');
      }

    } catch (error) {
      const errorMessage = error?.error?.message || error?.message || 'Something went wrong!';
      this.toastr.error(errorMessage, 'Error!');
    } finally {
      setTimeout(() => {
        this.spinner.hide().catch((err) => {
          console.error('Spinner hide error:', err);
        });
      }, 500);
    }
  }

  private validateDateRange(): void {
    const dateFrom = this.updateForm.controls.dateFrom.value;
    const dateTo = this.updateForm.controls.dateTo.value;
    
    if (dateFrom && dateTo) {
      const fromMoment = moment(dateFrom);
      const toMoment = moment(dateTo);
      
      if (fromMoment.isAfter(toMoment)) {
        // Show warning
        this.toastr.warning(
          `Date To (${toMoment.format('MMM DD, YYYY')}) cannot be earlier than Date From (${fromMoment.format('MMM DD, YYYY')})`,
          'Invalid Date Range',
          { timeOut: 4000 }
        );
      }
    }
  }

  private validateDateRangeForForm(): void {
    const dateFrom = this.updateForm.controls.dateFrom.value;
    const dateTo = this.updateForm.controls.dateTo.value;
    
    if (dateFrom && dateTo) {
      const selectedDateFrom = new Date(dateFrom);
      selectedDateFrom.setHours(0, 0, 0, 0);
      
      const selectedDateTo = new Date(dateTo);
      selectedDateTo.setHours(0, 0, 0, 0);
      
      // If Date To is earlier than Date From, mark form as having errors
      if (selectedDateTo < selectedDateFrom) {
        console.warn('Invalid date range detected:', {
          dateFrom: selectedDateFrom,
          dateTo: selectedDateTo
        });
      }
    }
  }
  
  private normalizeDate(date: any): string {
    if (!date) {
      return '';
    }
    
    try {
      const momentDate = moment(date);
      
      if (!momentDate.isValid()) {
        console.warn('Invalid date:', date);
        return '';
      }
      
      return momentDate.format('YYYY-MM-DD');
    } catch (error) {
      console.error('Error normalizing date:', date, error);
      return '';
    }
  }
}
